import React from 'react';
import PropTypes from 'prop-types';
import { optimizeImage, optimizeUnsplash, optimizeWikimedia, generateSrcSet } from '../../utils/imageOptimizer';

/**
 * OptimizedImage Component
 *
 * Automatically optimizes images from Unsplash, Wikimedia, and other sources
 * Features:
 * - Automatic WebP conversion
 * - Responsive srcSet generation
 * - Lazy loading support
 * - Explicit width/height for CLS prevention
 *
 * Usage:
 * <OptimizedImage
 *   src="https://images.unsplash.com/photo-xxx"
 *   alt="Description"
 *   width={800}
 *   height={600}
 * />
 */
const OptimizedImage = ({
  src,
  alt = '',
  width = 800,
  height = 600,
  className = '',
  lazy = true,
  sizes = '(max-width: 640px) 400px, (max-width: 1024px) 800px, 1200px',
  quality = 85,
  style = {},
  onClick,
  onLoad,
  onError
}) => {
  // Don't optimize if no src
  if (!src) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        width={width}
        height={height}
      />
    );
  }

  // Detect image source
  const isUnsplash = src.includes('unsplash.com');
  const isWikimedia = src.includes('wikimedia.org') || src.includes('wikipedia.org');

  // Generate optimized URL
  let optimizedUrl;
  if (isUnsplash) {
    optimizedUrl = optimizeUnsplash(src, { width, height, quality });
  } else if (isWikimedia) {
    optimizedUrl = optimizeWikimedia(src, { width, height, quality });
  } else if (src.startsWith('http')) {
    // External image - use CDN optimization
    optimizedUrl = optimizeImage(src, { width, height, quality, format: 'webp' });
  } else {
    // Local image - use as is
    optimizedUrl = src;
  }

  // Generate responsive srcSet
  const srcSet = src.startsWith('http') && !src.includes('safar_glimpse_logo')
    ? generateSrcSet(src, [400, 800, 1200], 'webp')
    : undefined;

  return (
    <img
      src={optimizedUrl}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      className={className}
      loading={lazy ? 'lazy' : 'eager'}
      width={width}
      height={height}
      decoding="async"
      style={style}
      onClick={onClick}
      onLoad={onLoad}
      onError={onError}
    />
  );
};

OptimizedImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  className: PropTypes.string,
  lazy: PropTypes.bool,
  sizes: PropTypes.string,
  quality: PropTypes.number,
  style: PropTypes.object,
  onClick: PropTypes.func,
  onLoad: PropTypes.func,
  onError: PropTypes.func
};

export default OptimizedImage;
